import React, { useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useUserProfileStore } from 'stores/userProfileStore';
import { useAuthStore } from 'stores/authStore';
import { useActiveCompanyStore } from 'stores/activeCompanyStore';
import { ROUTES } from 'utils/constants';
import { Loader2 } from 'lucide-react';
import styles from './CompanyGuard.module.scss';

interface CompanyGuardProps {
  children: React.ReactNode;
}

/**
 * Component that redirects users without a company to the company setup page.
 * This should wrap protected routes that require a company.
 */
function CompanyGuard({ children }: CompanyGuardProps) {
  const profile = useUserProfileStore((s) => s.profile);
  const isLoading = useUserProfileStore((s) => s.isLoading);
  const isAuth = useAuthStore((state) => state.isAuth);
  const activeCompanyId = useActiveCompanyStore((s) => s.activeCompanyId);
  const isActiveCompanyInitialized = useActiveCompanyStore(
    (s) => s.isInitialized,
  );
  const navigate = useNavigate();
  const location = useLocation();

  // Check if current route is a POS route (should work offline)
  const isPOSRoute = useMemo(() => {
    return (
      location.pathname.startsWith(ROUTES.POS.ROOT) ||
      location.pathname === ROUTES.POS.PAYMENT ||
      location.pathname === ROUTES.POS.SUCCESS
    );
  }, [location.pathname]);

  // Only check and redirect after loading is complete
  useEffect(() => {
    // Skip company check for POS routes (they work offline)
    if (isPOSRoute) {
      return;
    }

    if (!isAuth || isLoading) {
      return;
    }

    // If profile is loaded and user has companies, but they're on the setup page, redirect away
    if (
      profile !== null &&
      profile.companies &&
      profile.companies.length > 0 &&
      location.pathname === ROUTES.COMPANY.SETUP
    ) {
      navigate(ROUTES.DASHBOARD, { replace: true });
      return;
    }

    // Only redirect to setup if ALL conditions are met:
    // 1. User is authenticated
    // 2. Profile loading is complete
    // 3. Profile exists (not null - meaning it was loaded)
    // 4. Profile has no companies (empty array or undefined)
    // 5. Not already on the setup page
    if (
      profile !== null &&
      (!profile.companies || profile.companies.length === 0) &&
      location.pathname !== ROUTES.COMPANY.SETUP
    ) {
      navigate(ROUTES.COMPANY.SETUP, { replace: true });
    }
  }, [profile, isLoading, isAuth, navigate, location.pathname, isPOSRoute]);

  // POS routes can work offline without company check
  if (isPOSRoute) {
    return <>{children}</>;
  }

  // Profile lists at least one company: still wait for active company hydration
  // (storage decrypt + default selection in useActiveCompanyInit). Otherwise
  // company-scoped API calls can run without companyId and the API returns 400.
  const hasCompanies = Boolean(
    profile?.companies && profile.companies.length > 0,
  );
  const activeCompanyReady =
    isActiveCompanyInitialized && Boolean(activeCompanyId);

  if (profile && hasCompanies && isAuth && activeCompanyReady) {
    return <>{children}</>;
  }

  // If profile hasn't loaded yet (still null), don't render anything
  // This prevents premature rendering before we know if user has a company
  if (
    profile === null ||
    isLoading ||
    !isAuth ||
    !profile.companies ||
    profile.companies.length === 0
  ) {
    return (
      <div className={styles.loadingContainer}>
        <Loader2 className={styles.loadingIcon} size={48} />
        <p className={styles.loadingText}>Loading company access...</p>
      </div>
    );
  }

  // Has companies in profile but active company not hydrated yet
  if (hasCompanies && isAuth && !activeCompanyReady) {
    return (
      <div className={styles.loadingContainer}>
        <Loader2 className={styles.loadingIcon} size={48} />
        <p className={styles.loadingText}>Loading company access...</p>
      </div>
    );
  }

  return (
    <div className={styles.loadingContainer}>
      <Loader2 className={styles.loadingIcon} size={48} />
      <p className={styles.loadingText}>Loading company access...</p>
    </div>
  );
}

export default CompanyGuard;
